<script setup lang="ts">
import { ButtonType, type Position } from '../types/common';

defineProps({
    disabled: {
        type: Boolean,
        default: false
    },
    label: {
        type: String,
        default: ''
    },
    icon: {
        type: String,
        default: ''
    },
    iconPos: {
        type: String as PropType<Position>,
        default: undefined
    },
    iconRight: {
        type: String,
        default: ''
    },
    type: {
        type: String as PropType<ButtonType>,
        default: ButtonType.OUTLINE_PRIMARY
    },
    loading: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['click']);

const handleClick = (event: Event) => {
    emit('click', event);
};
</script>

<template>
    <VLButton
        :class="`button ${type.toLowerCase()}-button`"
        :label="label"
        :disabled="disabled"
        :icon="icon"
        :icon-pos="iconPos"
        :loading="loading"
        @click="handleClick($event)"
    >
        <template #icon>
            <slot name="icon" />
        </template>
        <template v-if="iconRight || $slots.iconRight" #default>
            {{ label }}
            <i v-if="iconRight" :class="iconRight" class="p-button-icon p-button-icon-right ml-2" />
            <slot v-else name="iconRight" />
        </template>
    </VLButton>
</template>

<style scoped lang="css">
.p-button {
    border: solid 1px transparent;
    box-sizing: border-box;
}
:deep(.p-button-label) {
    font-weight: 400;
}

.primary-button {
    background-color: var(--primary-color);
}
.primary-button:hover {
    border: solid 1px var(--primary-color);
    background: var(--primary-color-hover);
}

.primary-button:focus-visible {
    outline-color: var(--primary-color-hover);
}

.primary-button :deep(.p-button-icon-left), .primary-button :deep(.p-button-icon-right) {
    color: var(--white-color);
}

.primary-button :deep(.p-button-label) {
    color: var(--white-color);
}
.primary-button:disabled {
    background-color: var(--primary-color-disabled);
    color: var(--primary-text-disabled);
    border-color: var(--primary-color-disabled);
    cursor: not-allowed;
}

.primary-button:active:not(:hover) {
    background: var(--primary-color-hover);
}

.secondary-button {
    background-color: var(--secondary-color);
    border: solid 1px transparent;
    box-sizing: border-box;
}
.secondary-button:hover {
    border: solid 1px var(--secondary-color-hover);
    background-color: var(--secondary-color-hover);
}

.secondary-button:focus-visible {
    outline-color: var(--secondary-color-hover);
}
.secondary-button :deep(.p-button-icon-left), .secondary-button :deep(.p-button-icon-right) {
    color: var(--white-color);
}

.secondary-button :deep(.p-button-label) {
    color: var(--white-color);
}

.secondary-button:disabled {
    background-color: var(--secondary-color-disabled);
    border-color: var(--secondary-color-disabled);
    cursor: not-allowed;
}

.secondary-button:active:not(:hover) {
    background: var(--secondary-color-hover);
}

.outline_secondary-button {
    background-color: var(--white-color-transparent-70);
    border: solid 1px var(--secondary-color);
    color: var(--secondary-color);
}

.outline_secondary-button:hover {
    border: solid 1px var(--secondary-color-hover);
    background-color: var(--action-btn-hover);
    color: var(--secondary-color-hover);
}

.outline_secondary-button:not(:disabled):active {
    border-color: var(--secondary-color-hover);
    color: var(--secondary-color-hover);
}

.outline_secondary-button:active:not(:hover) {
    background: var(--action-btn-hover);
    color: var(--secondary-color-hover);
}

.outline_secondary-button :deep(.p-button-icon-left), .action-button :deep(.p-button-icon-right) {
    color: var(--secondary-color);
}

.outline_secondary-button :deep(.p-button-label) {
    color: var(--secondary-color);
}
.outline_secondary-button:disabled {
    background: var(--action-btn-hover);
    border-color: var(--action-btn-hover);
    cursor: not-allowed;
}

.outline_primary-button {
    background-color: var(--white-color-transparent-20);
    border: solid 1px var(--primary-color);
}

.outline_primary-button:hover {
    background-color: var( --cancel-btn-hover);
    border: solid 1px var(--primary-color-hover);
    color: solid 1px var(--primary-color-hover);
}

.outline_primary-button :deep(.p-button-icon-left), .cancel-button :deep(.p-button-icon-right) {
    color: var(--primary-color);
}

.outline_primary-button :deep(.p-button-label) {
    color: var(--primary-color);
}

.outline_primary-button :deep(.p-button-icon) {
    color: var(--primary-color);
}

.outline_primary-button:not(:disabled):active {
    border-color: var(--primary-color-hover)
}

.outline_primary-button:focus-visible {
    outline-color: var(--primary-color-hover);
}

.outline_primary-button:active:not(:hover) {
    background: var(--cancel-btn-hover);
}

.outline_primary-button:disabled {
    background: var(--cancel-btn-hover);
    border-color: var(--cancel-btn-hover);
    cursor: not-allowed;
}

.outline_danger-button {
    background-color: var(--white-color-transparent-70);
    border: solid 1px var(--primary-color);
    color: var(--primary-color);
}

.outline_danger-button:hover {
    background-color: var(--danger-btn-hover);
    border: solid 1px var(--danger-color);
    color: var(--danger-color);
}

.outline_danger-button:not(:disabled):active {
    border-color: var(--danger-color);
    color: var(--danger-color);
}

.outline_danger-button:active:not(:hover) {
    background: var(--danger-btn-hover);
}

.outline_danger-button:disabled {
    background: var(--danger-btn-hover);
    border-color: var(--danger-btn-hover);
    cursor: not-allowed;
}

.link_primary-button {
    background-color: transparent;
    border: 1px solid transparent;
    color: var(--primary-color);
}

.link_primary-button:hover {
    border: solid 1px var(--primary-transparent-3);
    background-color: var(--primary-transparent-3);
    color: var(--primary-color-hover);
}

.link_primary-button:not(:disabled):active {
    border: solid 1px var(--primary-transparent-3);
    color: var(--primary-color-hover);
}

.link_primary-button:active:not(:hover) {
    background-color: var(--primary-transparent-3);
}

.link_primary-button:disabled {
    background: var(--cancel-btn-hover);
    border-color: var(--cancel-btn-hover);
    cursor: not-allowed;
}

.link_danger-button {
    background-color: transparent;
    border: 1px solid transparent;
    color: var(--primary-color);
}

.link_danger-button:hover {
    border: solid 1px var(--danger-transparent-1);
    background-color: var(--danger-transparent-1);
    color: var(--danger-color);
}

.link_danger-button:not(:disabled):active {
    border: solid 1px var(--danger-transparent-1);
    color: var(--danger-color);
}

.link_danger-button:active:not(:hover) {
    background-color: var(--danger-transparent-1);
}

.link_danger-button:disabled {
    background: var(--danger-btn-hover);
    border-color: var(--danger-btn-hover);
    cursor: not-allowed;
}

.link_underline-button {
    background-color: transparent;
    border: 1px solid transparent;
    color: var(--primary-color);
}

.link_underline-button:deep(.p-button-label) {
    text-decoration: underline;
}

.link_underline-button:hover {
    border: solid 1px var(--primary-transparent-3);
    background-color: var(--primary-transparent-3);
    color: var(--primary-color-hover);
}

.link_underline-button:not(:disabled):active {
    border: solid 1px var(--primary-transparent-3);
    color: var(--primary-color-hover);
}

.link_underline-button:active:not(:hover) {
    background-color: var(--primary-transparent-3);
}

.link_underline-button:disabled {
    background: var(--cancel-btn-hover);
    border-color: var(--cancel-btn-hover);
    cursor: not-allowed;
}
</style>
